import React, { useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';
import CampoNumero from './CampoNumero';
import { numeroSeguro } from '../utils/validacion';

const fmt = (n) => `${simboloMoneda()} ${Number(n || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PRESETS_PCT = [40, 60, 80, 100];
const PRESETS_MULT = [2, 2.5, 3];

// Calculadora de precio: ayuda al emprendedor a decidir a cuánto vender
// para tener ganancia. Dos formas (elección de Merly): por % de ganancia
// sobre el costo, o multiplicando el costo. Además avisa si el precio
// actual está por debajo del costo (pérdida).
export default function CalculadoraPrecio({ costo, precioVenta, onAplicarPrecio, deshabilitado }) {
  const [modo, setModo] = useState('pct'); // 'pct' | 'mult'
  const [pct, setPct] = useState('60');
  const [mult, setMult] = useState('2');

  const costoNum = numeroSeguro(costo);
  const ventaNum = numeroSeguro(precioVenta);

  const factor = modo === 'pct'
    ? 1 + numeroSeguro(pct) / 100
    : numeroSeguro(mult);
  // Precio sugerido redondeado a lempira entera (números más "vendibles").
  const sugerido = costoNum > 0 ? Math.round(costoNum * factor) : 0;
  const gananciaSugerida = sugerido - costoNum;

  // Análisis del precio que ya escribió (si lo hay).
  const ganancia = ventaNum - costoNum;
  const pctReal = costoNum > 0 ? (ganancia / costoNum) * 100 : 0;

  const btn = (activo) => ({
    padding: '6px 12px', borderRadius: '999px', fontSize: '13px', fontWeight: 600, cursor: deshabilitado ? 'default' : 'pointer',
    border: `1px solid ${activo ? 'var(--color-primario,#DB2777)' : '#E5E7EB'}`,
    backgroundColor: activo ? 'var(--color-primario,#DB2777)' : 'white', color: activo ? 'white' : '#6B7280',
  });
  const chip = { padding: '6px 10px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: deshabilitado ? 'default' : 'pointer', border: '1px solid #FBCFE8', backgroundColor: 'white', color: 'var(--color-primario,#DB2777)' };
  const input = { width: '80px', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };

  return (
    <div style={{ marginBottom: '16px', padding: '14px', backgroundColor: '#F5F3FF', borderRadius: '10px', border: '1px solid #DDD6FE' }}>
      <p style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: 700, color: '#6D28D9' }}>
        🧮 ¿A cuánto vender? — calculadora de precio
      </p>

      {costoNum <= 0 ? (
        <p style={{ margin: 0, fontSize: '13px', color: '#6B7280' }}>
          Escribe primero el <strong>precio de costo</strong> arriba y aquí te sugiero a cuánto venderlo. 👆
        </p>
      ) : (
        <>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <button type="button" disabled={deshabilitado} onClick={() => setModo('pct')} style={btn(modo === 'pct')}>% de ganancia</button>
            <button type="button" disabled={deshabilitado} onClick={() => setModo('mult')} style={btn(modo === 'mult')}>Multiplicar costo</button>
          </div>

          {modo === 'pct' ? (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: '#374151' }}>Quiero ganar</span>
                <CampoNumero valor={pct} onChange={setPct} deshabilitado={deshabilitado}
                  decimales={0} min={0} max={1000} maxMensaje="Ese porcentaje es demasiado alto"
                  estilo={input} />
                <span style={{ fontSize: '13px', color: '#374151' }}>% sobre el costo</span>
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {PRESETS_PCT.map((p) => (
                  <button type="button" key={p} disabled={deshabilitado} onClick={() => setPct(String(p))} style={chip}>{p}%</button>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: '#374151' }}>Vender a</span>
                <CampoNumero valor={mult} onChange={setMult} deshabilitado={deshabilitado}
                  decimales={1} min={1} minSiVacio max={50} maxMensaje="Como máximo 50 veces el costo"
                  estilo={input} />
                <span style={{ fontSize: '13px', color: '#374151' }}>veces el costo</span>
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {PRESETS_MULT.map((m) => (
                  <button type="button" key={m} disabled={deshabilitado} onClick={() => setMult(String(m))} style={chip}>x{m}</button>
                ))}
              </div>
            </div>
          )}

          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '12px', border: '1px solid #E5E7EB' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
              <div>
                <p style={{ margin: 0, fontSize: '12px', color: '#6B7280' }}>Precio sugerido</p>
                <p style={{ margin: 0, fontSize: '22px', fontWeight: 'bold', color: 'var(--k-verde)' }}>{fmt(sugerido)}</p>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6B7280' }}>Ganás {fmt(gananciaSugerida)} por unidad</p>
              </div>
              <button type="button" disabled={deshabilitado} onClick={() => onAplicarPrecio(String(sugerido))}
                style={{ padding: '10px 14px', backgroundColor: 'var(--k-verde)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: deshabilitado ? 'default' : 'pointer' }}>
                Usar este precio
              </button>
            </div>
          </div>

          {ventaNum > 0 && (
            ventaNum < costoNum ? (
              <p style={{ margin: '10px 0 0', padding: '8px 10px', backgroundColor: '#FEE2E2', borderRadius: '8px', fontSize: '13px', color: '#991B1B', fontWeight: 600 }}>
                ⚠️ Estás vendiendo por debajo del costo: perderías {fmt(costoNum - ventaNum)} por unidad.
              </p>
            ) : ventaNum === costoNum ? (
              <p style={{ margin: '10px 0 0', padding: '8px 10px', backgroundColor: '#FEF3C7', borderRadius: '8px', fontSize: '13px', color: '#92400E', fontWeight: 600 }}>
                ⚠️ A este precio no ganás nada (vendés al mismo costo).
              </p>
            ) : (
              <p style={{ margin: '10px 0 0', padding: '8px 10px', backgroundColor: '#ECFDF5', borderRadius: '8px', fontSize: '13px', color: '#065F46', fontWeight: 600 }}>
                ✅ Con tu precio de {fmt(ventaNum)} ganás {fmt(ganancia)} por unidad ({Math.round(pctReal)}% sobre el costo).
              </p>
            )
          )}
        </>
      )}
    </div>
  );
}
